import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import type { CatalogSeriesConfigurator } from './types'

/**
 * The narrow seam `SeriesConfiguratorDrawer` needs (ADR-0153 D4/D5/D7,
 * UID-322) — `GET /catalog/categories/{category}/series/{seriesCode}/configurator`,
 * mirroring `useCatalogSeries.ts`'s `CatalogSeriesService` injectable-seam
 * pattern so tests never hit the network.
 *
 * Returns `null` for the structural "this category does not do drawer
 * browse" case (the upstream 404 `catalog/browse_mode_not_enabled`) — the
 * same `null`-means-no-such-resource convention the port itself documents.
 * `CatalogSeriesPage`'s `configuratorReady` gate should make this
 * unreachable in practice; this seam still has to answer it defensively
 * rather than surface it as a generic error.
 */
export interface CatalogSeriesConfiguratorService {
  getConfigurator(
    category: string,
    seriesCode: string,
    specs?: Readonly<Record<string, string>>,
    /**
     * UID-322 — the caller's NUMERIC picks on the series' continuous
     * dimensions, a separate matcher from `specs` (containment vs.
     * equality — see `CatalogSeriesConfiguratorAxisInterval`'s doc).
     * Optional and additive: a caller with no numeric picks omits it.
     */
    dims?: Readonly<Record<string, number>>,
  ): Promise<CatalogSeriesConfigurator | null>
}

function problemTitle(error: unknown, fallback: string): string {
  return typeof error === 'object' && error !== null && 'title' in error
    ? String((error as { title?: unknown }).title)
    : fallback
}

async function bffGetConfigurator(
  category: string,
  seriesCode: string,
  specs?: Readonly<Record<string, string>>,
  dims?: Readonly<Record<string, number>>,
): Promise<CatalogSeriesConfigurator | null> {
  const { data, error } = await apiClient.GET(
    '/catalog/categories/{category}/series/{seriesCode}/configurator',
    {
      params: {
        path: { category, seriesCode },
        query: {
          ...(specs !== undefined && Object.keys(specs).length > 0 ? { specs } : {}),
          ...(dims !== undefined && Object.keys(dims).length > 0 ? { dims } : {}),
        },
      },
    },
  )
  if (data) return data

  // ADR-0153 D3 — while the opt-in table holds no row (or none for this
  // category) this resource does not exist for ANY series, structurally,
  // not merely unused. `CatalogSeriesPage`'s `configuratorReady` gate
  // should keep this drawer from ever mounting for such a series, but a
  // defensive caller treats it the same way `resolveConfigurable` treats
  // an unresolvable SKU: `null`, not a thrown error.
  if (error?.status === 404) return null

  throw new Error(problemTitle(error, 'We couldn’t load this series’ configurator.'))
}

export const defaultCatalogSeriesConfiguratorService: CatalogSeriesConfiguratorService = {
  getConfigurator: bffGetConfigurator,
}

export type SeriesConfiguratorState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  /** The structural "not opted in" case — see the service doc above. */
  | { readonly status: 'not_enabled' }
  | { readonly status: 'ready'; readonly configurator: CatalogSeriesConfigurator }

export type UseSeriesConfiguratorResult = SeriesConfiguratorState & {
  readonly retry: () => void
}

/**
 * Data-layer seam for `SeriesConfiguratorDrawer` (ADR-0153 D4/D5/D7, UID-322
 * — superseding UID-460's client-side `catalog-series-config-merge.ts`).
 * Re-fetches whenever `seriesCode` changes (a different series opened) or
 * `appliedSpecs`/`appliedDims`'s CONTENT changes (a pick or a clear) — same
 * serialized-content memo trick `useCatalogSeries.ts` uses for `selection`,
 * so a caller rebuilding an equal `appliedSpecs`/`appliedDims` object every
 * render (as the drawer does, deriving them from local `useState`) doesn't
 * retrigger a fetch it didn't ask for.
 *
 * UID-322 — the unified facet model means `specs` and `dims` narrow the SAME
 * response together (`candidateCount` reflects both), and once it reaches 1
 * the response's own `resolvedCard` is the caller's row — there is no
 * second seam to call for that.
 */
export function useSeriesConfigurator(
  category: string,
  seriesCode: string,
  appliedSpecs: Readonly<Record<string, string>> = {},
  appliedDims: Readonly<Record<string, number>> = {},
  service: CatalogSeriesConfiguratorService = defaultCatalogSeriesConfiguratorService,
): UseSeriesConfiguratorResult {
  const specsKey = JSON.stringify(appliedSpecs)
  const dimsKey = JSON.stringify(appliedDims)
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on `specsKey`/`dimsKey` (serialized content), not object identity — see the doc comment above.
  const stableSpecs = useMemo(() => appliedSpecs, [specsKey])
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on `specsKey`/`dimsKey` (serialized content), not object identity — see the doc comment above.
  const stableDims = useMemo(() => appliedDims, [dimsKey])
  const hasSpecs = Object.keys(stableSpecs).length > 0
  const hasDims = Object.keys(stableDims).length > 0
  const [state, setState] = useState<SeriesConfiguratorState>({ status: 'loading' })
  const serviceRef = useRef(service)
  serviceRef.current = service

  const load = useCallback(() => {
    setState({ status: 'loading' })
    let cancelled = false
    serviceRef.current
      .getConfigurator(
        category,
        seriesCode,
        hasSpecs ? stableSpecs : undefined,
        hasDims ? stableDims : undefined,
      )
      .then((configurator) => {
        if (cancelled) return
        setState(configurator ? { status: 'ready', configurator } : { status: 'not_enabled' })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const message =
          error instanceof Error ? error.message : 'We couldn’t load this series’ configurator.'
        setState({ status: 'error', message })
      })
    return () => {
      cancelled = true
    }
    // `service` is read only via the ref, so `category`/`seriesCode`/`stableSpecs`/`stableDims`/`hasSpecs`/`hasDims` are the only real dependencies.
  }, [category, seriesCode, stableSpecs, stableDims, hasSpecs, hasDims])

  useEffect(() => load(), [load])

  return { ...state, retry: load }
}
